import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useLocation, useParams } from "react-router-dom";

import { captureEvent, tierLabel } from "../analytics";
import { getScore, saveProperty } from "../api/client";
import { AccountGate } from "../components/AccountGate";
import { ChatPanel } from "../components/ChatPanel";
import { CostToOwnPanel } from "../components/CostToOwnPanel";
import { Disclaimer } from "../components/Disclaimer";
import { ErrorState } from "../components/ErrorState";
import { FlipRiskPanel } from "../components/FlipRiskPanel";
import { LoadingState } from "../components/LoadingState";
import { NavBar } from "../components/NavBar";
import { PropertyMap } from "../components/PropertyMap";
import { PropertySummaryCard } from "../components/PropertySummaryCard";
import { ProScorePanel } from "../components/ProScorePanel";
import { FactorCardView } from "../components/ScoreResult";
import { useAuth } from "../auth/useAuth";
import type { ScoreResponse } from "../types/api";

/**
 * ScorePage — /score/{score_id} shared Tilt Report (#37).
 *
 * A shared report is a HOT LEAD surface (often the Pro subscriber's spouse).
 * The report itself is already paid for, so it renders entirely from persisted
 * Dynamo/S3 data at ZERO live API cost — no place_id re-resolution (coords ride
 * the response now), no web search (licensed facts are persisted). The one
 * allowed call is the macro FRED mortgage rate in Cost-to-Own (free, cached,
 * not property-specific).
 *
 * Tiering (maps onto the existing free/paid model — no new tier):
 *   - Logged-out: teaser (address + map + score + facts); the deeper analysis
 *     is blurred behind a soft "create a free account" gate. Sign up (email) to
 *     read the full report.
 *   - Free (signed up): full static report. Chat + Save are LOCKED → Go Pro.
 *   - Pro: full report + live chat + save. A New Search still runs the normal
 *     free/paid flow for that user — nothing about the shared report is sticky.
 *
 * The AI brief is the persisted narrative (`result.narrative`) rendered
 * read-only — we never re-stream the analyst here (that endpoint is Pro-gated
 * and a shared report shouldn't spend an investigation on every view).
 */
export function ScorePage() {
  const { scoreId } = useParams<{ scoreId: string }>();
  const location = useLocation();
  const auth = useAuth();
  const [result, setResult] = useState<ScoreResponse | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  // Sign in but COME BACK HERE. The Cognito Managed Login round-trip otherwise
  // lands the user on the home search (AuthCallback's default); stashing this
  // report's path makes it return to the shared report, where the effect below
  // refetches the now-authenticated full report from cache (#37).
  const signInReturningHere = () => {
    try {
      sessionStorage.setItem(
        "pt.postLoginRedirect",
        location.pathname + location.search,
      );
    } catch {
      // sessionStorage unavailable — sign-in still works, just lands on home.
    }
    void auth.signIn();
  };

  useEffect(() => {
    // Wait for auth to settle before fetching: the GET /score/{id} endpoint
    // returns a logged-out TEASER vs the full report based on the Bearer token
    // (#37). Fetching mid-resolution would request anonymously, then need a
    // second pass. Re-running on auth.user change is what swaps teaser → full
    // the moment a visitor signs in.
    if (!scoreId || auth.loading) return;
    let cancelled = false;
    // No chat history on a shared report — it's a read-only snapshot, and a
    // viewer must not inherit the owner's chat. Pro viewers start fresh.
    getScore(scoreId, false)
      .then((r) => {
        if (!cancelled) {
          setResult(r);
          // Fires again when a visitor signs in mid-view (teaser → full);
          // `locked` distinguishes the two.
          captureEvent("report_viewed", {
            tier: tierLabel(auth.tier),
            context: "shared",
            locked: !auth.user,
            quadrant: r.tilt_map?.quadrant,
          });
        }
      })
      .catch((e: { code?: string; message?: string }) => {
        if (!cancelled) {
          setError({
            code: e.code || "unknown",
            message: e.message || "Failed to load score.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // auth.tier is read only for the analytics label; refetching on a tier
    // flip would change teaser/full behavior this effect deliberately keys
    // on auth.user alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreId, auth.loading, auth.user]);

  if (error) {
    return (
      <div className="app">
        <ErrorState code={error.code} message={error.message} />
        <Disclaimer />
      </div>
    );
  }
  if (!result || auth.loading) return <LoadingState message="Loading report…" />;

  // Soft gate on the deeper analysis: blurred until a logged-out visitor signs
  // up. The report body is then full for any signed-in account (free or paid).
  const locked = !auth.user;
  const isPaid = auth.tier === "paid";

  // Coords are persisted on the score (0 live calls). null on legacy records.
  const hasCoords = result.lat != null && result.lon != null;

  const proExtras = {
    beds: result.beds,
    baths: result.baths,
    sqft: result.living_sqft,
    pricePerSqft: result.living_sqft ? result.asking_price / result.living_sqft : null,
    yearBuilt: result.year_built,
    lotSizeAcres: result.lot_size_acres,
    lastSalePrice: result.last_sale_price,
    lastSaleDate: result.last_sale_date,
    avmValue: result.avm_value,
    avmChangePct: result.avm_12mo_change_pct,
    avmConfidence: result.avm_confidence,
    floodZone: result.flood_zone,
    equityPct: result.equity_pct,
    ownerOccupied: result.owner_occupied,
    annualTax: result.annual_tax,
    assessedValue: result.assessed_value,
    marketValue: result.market_value,
    permitCount: result.recent_permit_count,
    nriHazards: result.nri_hazards,
    crimeIndex: result.crime_index,
    airQualityIndex: result.air_quality_index,
    ownerOccupancyPct: result.owner_occupancy_pct,
    vacancyPct: result.vacancy_pct,
    medianCommuteMin: result.median_commute_min,
    medianHouseholdIncome: result.median_household_income,
    bachelorsPlusPct: result.bachelors_plus_pct,
    walkScore: result.walk_score,
    soundscore: result.soundscore,
  };

  const hasNarrative = Boolean(result.narrative && result.narrative.trim());

  return (
    <article className="app paid-property-view">
      {/* Authenticated users get the full NavBar so they can navigate back to
          their account, saved properties, etc. Logged-out visitors get a
          minimal linked header (the logo routes to app home → sign-in). */}
      {auth.user ? (
        <NavBar />
      ) : (
        <header className="app-header">
          <Link to="/" className="nav-brand" aria-label="PriceTilt home">
            PriceTilt<span className="nav-brand-mark" aria-hidden />
          </Link>
          <p className="tagline">Shared Tilt Report</p>
        </header>
      )}

      {/* Save action for authenticated Pro users viewing a shared report. */}
      {auth.user && isPaid && result.place_id && (
        <div className="shared-report-actions">
          <SharedSaveButton
            placeId={result.place_id}
            address={result.address}
            askingPrice={result.asking_price}
          />
        </div>
      )}

      {/* At-a-glance: address + map (the teaser, always visible). */}
      <section className="at-a-glance" aria-label="Property at a glance">
        <h2 className="property-address">{result.address}</h2>
        {hasCoords ? (
          <PropertyMap lat={result.lat as number} lon={result.lon as number} address={result.address} />
        ) : (
          <div className="property-map-placeholder" role="img" aria-label="Map unavailable">
            <span>Map unavailable</span>
          </div>
        )}
      </section>

      {/* Score + offer range — the hook, always visible. */}
      <div className="pro-summary-tile">
        <ProScorePanel
          scoreId={result.score_id}
          offerRange={result.offer_range}
          tiltMap={result.tilt_map}
          confidence={result.confidence}
          initialAskingPrice={result.asking_price}
          address={result.address}
        />
      </div>

      {/* Everything below is the deeper analysis — the property facts, the AI
          brief, factor breakdown, flip watch, and cost-to-own. For a logged-out
          visitor the server returns NONE of this data (a real gate, not a blur),
          so we render greyed placeholders behind the signup overlay. Any
          signed-in account gets the full report, rendered from cached data. */}
      <AccountGate locked={locked} onSignIn={signInReturningHere}>
        {locked ? (
          <GatedPreview />
        ) : (
          <>
            <PropertySummaryCard
              placeId={result.place_id}
              address={result.address}
              pro={proExtras}
            />

            {hasNarrative && (
              <section className="pro-narrative" aria-label="What the data shows">
                <h3>What the data shows</h3>
                <div className="chat-markdown">
                  <ReactMarkdown>{result.narrative}</ReactMarkdown>
                </div>
              </section>
            )}

            <section className="pro-tiles" aria-label="Factor breakdown">
              <div className="factor-grid">
                {result.factors.map((f) => (
                  <FactorCardView key={f.name} factor={f} />
                ))}
              </div>
            </section>

            {result.flip_risk && <FlipRiskPanel risk={result.flip_risk} />}

            <CostToOwnPanel askingPrice={result.asking_price} closingCost={result.closing_cost} scoreId={result.score_id} />
          </>
        )}
      </AccountGate>

      {/* The live AI analyst — working for Pro, locked invite for everyone else
          (#37). Logged-out visitors are first nudged to sign up by the gate
          above; the chat is the Pro hook. A fresh session keyed to this score
          (no initialHistory) — a viewer never inherits the owner's chat. */}
      {isPaid ? (
        <section className="pro-chat" aria-label="Ask the AI analyst">
          <h3>Ask the AI analyst</h3>
          <ChatPanel scoreId={result.score_id} />
        </section>
      ) : (
        <section className="shared-report-upsell" aria-label="Go deeper with PriceTilt Pro">
          <h3>Want to dig deeper?</h3>
          <p>
            PriceTilt Pro adds a real-time AI analyst you can chat with about
            this property, plus up to 15 verified reports a month — save,
            compare, and get alerts as you hunt.
          </p>
          <div className="locked-chat" aria-hidden>
            <div className="locked-chat-input">Ask anything about this property…</div>
            <span className="locked-chat-lock">🔒 Pro</span>
          </div>
          <div className="shared-report-upsell-actions">
            {locked ? (
              <button type="button" className="upgrade-cta" onClick={signInReturningHere}>
                Create a free account
              </button>
            ) : (
              <Link className="upgrade-cta" to="/upgrade">
                Upgrade to Pro
              </Link>
            )}
            <Link className="link-button" to="/">
              Score your own property →
            </Link>
          </div>
        </section>
      )}

      <Disclaimer />
    </article>
  );
}

/**
 * Save button for the shared report page — lets a Pro user save someone else's
 * shared report to their own saved properties list. Same pattern as the
 * SaveButton in PaidPropertyView but standalone for this page.
 */
function SharedSaveButton({
  placeId,
  address,
  askingPrice,
}: {
  placeId: string;
  address: string;
  askingPrice: number;
}) {
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  async function handleSave() {
    setState("saving");
    try {
      await saveProperty(placeId, address, undefined, askingPrice);
      setState("saved");
    } catch {
      setState("error");
    }
  }
  if (state === "saved") return <span className="saved-confirm">✓ Saved to your properties</span>;
  return (
    <button
      type="button"
      className="save-property-btn"
      onClick={() => void handleSave()}
      disabled={state === "saving"}
    >
      {state === "saving" ? "Saving…" : state === "error" ? "Retry save" : "★ Save to my properties"}
    </button>
  );
}

/**
 * Greyed, signed-out preview of the gated report. The real narrative, factor
 * cards, and facts are withheld at the server for logged-out visitors (#37), so
 * there's no real data to blur here — this is a representative skeleton that,
 * behind the AccountGate's blur + signup overlay, reads as "there's a full
 * report here." It carries no property data.
 */
function GatedPreview() {
  return (
    <div className="shared-report-skeleton" aria-hidden>
      <div className="srs-block srs-facts">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="srs-fact" />
        ))}
      </div>
      <div className="srs-line srs-line-wide" />
      <div className="srs-line" />
      <div className="srs-line" />
      <div className="srs-line srs-line-short" />
      <div className="srs-block srs-tiles">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="srs-tile" />
        ))}
      </div>
    </div>
  );
}
